import React, { createContext, useState, useCallback, useMemo } from 'react';
import { get, pick, set } from 'lodash-es';

const ALWAYS = () => true;
const IF_AGRONOMIST = (user) => user.isAgronomist;

const attributes = [
  { name: 'username', required: ALWAYS },
  { name: 'firstName', required: ALWAYS },
  { name: 'lastName', required: ALWAYS },
  { name: 'email', required: ALWAYS },
  { name: 'phone', required: ALWAYS },
  { name: 'roles', required: ALWAYS, default: [] },
  { name: 'organizations', required: ALWAYS, default: [] },
  { name: 'addressLine1' },
  { name: 'addressLine2' },
  { name: 'city' },
  { name: 'state' },
  { name: 'zip' },
  { name: 'sub' },
  { name: 'ccaLicenses', default: [] },
  { name: 'farmgate.groupId', required: IF_AGRONOMIST },
  { name: 'farmgate.regionId', required: IF_AGRONOMIST },
  { name: 'farmgate.areaBusinessManager', required: IF_AGRONOMIST },
  { name: 'farmgate.regionalBusinessDirector', required: IF_AGRONOMIST },
  { name: 'farmgate.partners', default: [] },
  { name: 'farmgate.yearlyBusinessGoal' },
];

const emptyUser = attributes.reduce(
  (memo, attribute) => set(memo, attribute.name, attribute.default || ''),
  {},
);

const Context = createContext();

export function Provider(props) {
  const { handleSave, handleClose, farmgate, roles, organizations } = props;
  const [isLoading, setLoading] = useState(true);
  const [user, setUser] = useState({
    ...structuredClone(emptyUser),
    ...pick(
      props.user,
      attributes.map((a) => a.name),
    ),
  });

  const [error, setError] = useState();
  const isNew = useMemo(() => !user.sub, [user.sub]);

  const updateUser = useCallback(({ name, value }) => {
    setUser((current) => {
      const updated = set(
        current,
        name,
        name === 'roles' ? [value].flat() : value,
      );
      return {
        ...updated,
        isAgronomist: updated.roles.some((role) => role.isAgronomist),
      };
    });
  }, []);

  const addFarmgateDetails = useCallback((farmgateUser) => {
    const farmgateAttributes = attributes
      .filter((attr) => attr.name.startsWith('farmgate.'))
      .map((attr) => attr.name.replace('farmgate.', ''));

    setUser((current) => ({
      ...current,
      farmgate: pick(farmgateUser, farmgateAttributes),
    }));
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handleError = useCallback((e) => {
    setError(e);
  }, []);

  const hasValue = useCallback(
    (attribute) => {
      const value = get(user, attribute);
      if (typeof value === 'number') return true;
      if (typeof value === 'string') return Boolean(value.trim());
      if (Array.isArray(value)) return value.length > 0;
      return false;
    },
    [user],
  );

  const canSave = useMemo(
    () =>
      attributes.every((attribute) =>
        attribute.required?.(user) ? hasValue(attribute.name) : true,
      ),
    [hasValue, user],
  );

  const context = {
    addFarmgateDetails,
    canSave,
    clearError,
    error,
    farmgate,
    handleClose,
    handleError,
    handleSave,
    isLoading,
    isNew,
    organizations,
    roles,
    setError,
    setLoading,
    updateUser,
    user,
  };

  return <Context.Provider value={context}>{props.children}</Context.Provider>;
}

export function useContext() {
  return React.useContext(Context);
}
